/**
 * Store and hooks for Simplify Nav New User spotlight state (ACTIVATION-2059).
 * Centralizes hasOpenedCustomize so we can clean up in one place if the experiment is abandoned.
 */
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const getInitialHasOpenedCustomize = (): boolean => {
	try {
		return (
			localStorage?.getItemAsBoolean(
				localStorageKeys.SIMPLIFY_NAV_NEW_USER_SPOTLIGHT_CUSTOMIZE_OPENED,
			) ?? false
		);
	} catch {
		return false;
	}
};

const getInitialHasOpenedCustomizeXflow = (): boolean => {
	try {
		return (
			localStorage?.getItemAsBoolean(
				localStorageKeys.SIMPLIFY_NAV_XFLOW_SPOTLIGHT_CUSTOMIZE_OPENED,
			) ?? false
		);
	} catch {
		return false;
	}
};

const SOURCE_CONFIG = {
	'new-user': {
		stateKey: 'hasOpenedCustomize' as const,
		storageKey: localStorageKeys.SIMPLIFY_NAV_NEW_USER_SPOTLIGHT_CUSTOMIZE_OPENED,
	},
	xflow: {
		stateKey: 'hasOpenedCustomizeXflow' as const,
		storageKey: localStorageKeys.SIMPLIFY_NAV_XFLOW_SPOTLIGHT_CUSTOMIZE_OPENED,
	},
};

const initialState = {
	hasOpenedCustomize: getInitialHasOpenedCustomize(),
	hasOpenedCustomizeXflow: getInitialHasOpenedCustomizeXflow(),
};

export const SimplifyNavNewUserSpotlightStore = createStore({
	initialState,
	actions: {
		setHasOpenedCustomize:
			(source: 'new-user' | 'xflow' = 'new-user') =>
			({ setState, getState }) => {
				const { stateKey, storageKey } = SOURCE_CONFIG[source];
				if (getState()[stateKey]) {
					return;
				}

				if (source === 'xflow') {
					setState({ hasOpenedCustomizeXflow: true });
				} else {
					setState({ hasOpenedCustomize: true });
				}

				try {
					localStorage?.setItem(storageKey, 'true');
				} catch {
					// storage unavailable
				}
			},
	},
	name: 'simplifyNavNewUserSpotlightStore',
});

export type SpotlightSource = 'new-user' | 'xflow' | null;

export const useSimplifyNavNewUserSpotlightStore = createHook(SimplifyNavNewUserSpotlightStore, {
	selector: (state) => state.hasOpenedCustomize,
});

export const useSimplifyNavSpotlightStore = createHook(SimplifyNavNewUserSpotlightStore, {
	selector: (state, source: SpotlightSource) =>
		source === 'xflow' ? state.hasOpenedCustomizeXflow : state.hasOpenedCustomize,
});

export const useSimplifyNavNewUserSpotlightStoreActions = createActionsHook(
	SimplifyNavNewUserSpotlightStore,
);
